import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UsersService } from './data-access/services/users.service';
import { User } from './data-access/models/user.model';
import { ModalMessageComponent } from '../../shared/ui/components/modal-message/modal-message.component';
import { ModalConfirmComponent, ConfirmButton } from '../../shared/ui/components/modal-confirm/modal-confirm.component';
import { ModalCreateUserComponent } from './components/modal-create-user.component';
import { OauthService } from '../login/data-access/services/oauth.service';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalMessageComponent, ModalConfirmComponent, ModalCreateUserComponent],
  templateUrl: './users.component.html',
  styles: []
})
export class UsersComponent implements OnInit {
  editingIndex: number | null = null;
  originalUser: User | null = null;
  users: User[] = [];
  responsables: User[] = []; // Lista de usuarios que son responsables
  currentUser: any = null;

  // Estados para los modales
  showMessageModal = false;
  messageModalTitle = '';
  messageModalMessage = '';
  messageModalType: 'success' | 'error' | 'info' | 'warning' = 'info';

  showConfirmModal = false;
  confirmModalTitle = '';
  confirmModalMessage = '';
  confirmModalType: 'danger' | 'warning' | 'info' = 'danger';
  confirmButtons: ConfirmButton[] = [];
  confirmAction: (() => void) | null = null;

  showCreateUserModal = false;

  constructor(
    private usersService: UsersService,
    private oauthService: OauthService
  ) { }

  ngOnInit() {
    this.loadUsers();
    this.loadCurrentUser();
  }

  loadCurrentUser() {
    this.oauthService.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUser = user;
      },
      error: (error) => {
        console.error('Error cargando usuario actual:', error);
      }
    });
  }

  loadUsers() {
    this.usersService.getUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.responsables = users.filter(u => u.manager_id === null);
      },
      error: (error) => {
        console.error('Error cargando usuarios:', error);
        this.showMessage('Error', 'No se pudieron cargar los usuarios', 'error');
      }
    });
  }

  getResponsableName(managerId: number | null): string {
    if (managerId === null) return '';
    const manager = this.users.find(u => u.id === managerId);
    return manager ? manager.name : '';
  }

  // Verificar si un usuario es responsable
  isResponsable(user: User): boolean {
    return user.manager_id === null;
  }

  // Toggle para cambiar entre responsable y no responsable
  toggleResponsable(user: User) {
    if (user.manager_id === null) {
      user.manager_id = this.responsables.find(r => r.id !== user.id)?.id || null;
    } else {
      user.manager_id = null;
    }
  }

  // Manejar cambio en el select de responsable
  onResponsableChange(user: User, value: string) {
    if (value === 'null' || value === '') {
      user.manager_id = null;
    } else {
      user.manager_id = Number(value);
    }
  }

  // Obtener responsables filtrados por tipo de agente
  getResponsables(): User[] {
    return this.responsables;
  }

  // Comparar valores para el select de responsable
  compareResponsable(val1: number | null, val2: number | null): boolean {
    return val1 === val2;
  }

  editUser(index: number) {
    this.editingIndex = index;
    // Guardar copia del usuario original para poder cancelar
    this.originalUser = { ...this.users[index] };
  }

  saveUser() {
    if (this.editingIndex !== null) {
      const user = this.users[this.editingIndex];
      this.usersService.updateUser(user).subscribe({
        next: (updatedUser) => {
          this.users[this.editingIndex!] = updatedUser;
          this.showMessage('Usuario guardado', 'Los cambios se han actualizado correctamente', 'success');
          this.editingIndex = null;
          this.originalUser = null;
        },
        error: (error) => {
          this.showMessage('Error', 'No se pudo actualizar el usuario', 'error');
          console.error('Error guardando usuario:', error);
          this.editingIndex = null;
          this.originalUser = null;
        }
      });
    }
  }

  cancelEdit() {
    if (this.editingIndex !== null && this.originalUser) {
      // Restaurar el usuario original
      this.users[this.editingIndex] = { ...this.originalUser };
    }
    this.editingIndex = null;
    this.originalUser = null;
  }

  deleteUser(index: number) {
    const user = this.users[index];
    this.showConfirm(
      'Eliminar usuario',
      `¿Estás seguro de que quieres eliminar al usuario ${user.name}?`,
      () => {
        this.usersService.deleteUser(user.id).subscribe({
          next: () => {
            this.users.splice(index, 1);
            this.showMessage('Éxito', 'Usuario eliminado correctamente', 'success');
          },
          error: (error) => {
            this.showMessage('Error', 'No se pudo eliminar el usuario', 'error');
            console.error('Error eliminando usuario:', error);
          }
        });
      },
      'danger',
      'Eliminar'
    );
  }

  // Métodos para manejar modales
  showMessage(title: string, message: string, type: 'success' | 'error' | 'info' | 'warning') {
    this.messageModalTitle = title;
    this.messageModalMessage = message;
    this.messageModalType = type;
    this.showMessageModal = true;
  }

  closeMessageModal() {
    this.showMessageModal = false;
  }

  showConfirm(title: string, message: string, action: () => void, type: 'danger' | 'warning' | 'info' = 'danger', confirmText: string = 'Aceptar') {
    this.confirmModalTitle = title;
    this.confirmModalMessage = message;
    this.confirmModalType = type;
    this.confirmAction = action;
    this.confirmButtons = [
      { text: 'Cancelar', action: 'cancel' },
      { text: confirmText, action: 'confirm', style: type === 'info' ? 'primary' : type }
    ];
    this.showConfirmModal = true;
  }

  onConfirmAction(actionName: string) {
    if (actionName === 'confirm' && this.confirmAction) {
      this.confirmAction();
    }
    this.closeConfirmModal();
  }

  closeConfirmModal() {
    this.showConfirmModal = false;
    this.confirmAction = null;
    this.confirmButtons = [];
  }

  openCreateUserModal() {
    this.showCreateUserModal = true;
  }

  closeCreateUserModal() {
    this.showCreateUserModal = false;
  }

  createUser(newUser: { nombre: string; email: string; telefono: string; exten: string; job_title_id: number; responsable: number | null }) {
    const userToCreate = {
      name: newUser.nombre,
      email: newUser.email,
      phone: newUser.telefono,
      exten: newUser.exten,
      job_title_id: newUser.job_title_id,
      manager_id: newUser.responsable
    };
    
    this.usersService.createUser(userToCreate).subscribe({
      next: (createdUser) => {
        this.closeCreateUserModal();
        this.users.push(createdUser);
        this.showMessage('Éxito', 'Usuario creado correctamente', 'success');
      },
      error: (error) => {
        this.showMessage('Error', 'No se pudo crear el usuario', 'error');
        console.error('Error creando usuario:', error);
      }
    });
  }

  toggleAdmin(user: User, event: Event) {
    event.preventDefault();
    
    const newStatus = !user.is_superadmin;
    const message = newStatus 
      ? `¿Estás seguro de que quieres dar permisos de administrador a ${user.name}?`
      : `¿Estás seguro de que quieres quitar los permisos de administrador a ${user.name}?`;
    
    this.showConfirm(
      'Cambiar permisos de administrador',
      message,
      () => {
        user.is_superadmin = newStatus;
        this.usersService.updateUser(user).subscribe({
          next: () => {
            const status = user.is_superadmin ? 'administrador' : 'usuario normal';
            this.showMessage('Permisos actualizados', `El usuario ahora es ${status}`, 'success');
          },
          error: (error) => {
            // Revertir el cambio en caso de error
            user.is_superadmin = !newStatus;
            this.showMessage('Error', 'No se pudieron actualizar los permisos', 'error');
            console.error('Error actualizando permisos:', error);
          }
        });
      },
      'warning',
      'Cambiar'
    );
  }

  isCurrentUserAdmin(): boolean {
    if (!this.currentUser || !this.currentUser.roles) {
      return false;
    }
    return this.currentUser.roles.some((role: any) => role.id === 1);
  }
}